<template>
  <div class="error-page-layout">
    <div class="error-page">
      <div class="content">
        <span
          id="status-code"
          data-testid="error-status-code"
        >
          {{ statusCodeMessage }}
        </span>
        <h1>
          {{ dataByStatusCode.title }}
        </h1>
        <client-only>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p v-html="sanitizedErrorMessage" />
        </client-only>
        <div id="go-Home-wrapper">
          <a
            :href="homePath"
            :style="{ backgroundColor: buttonColors }"
          >
            {{ $t('pages.shared.returnHome') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import DOMPurify from 'dompurify';
import get from 'lodash/get';
import { computed, onMounted } from 'vue';
import { useMainStore } from '../../stores/mainStore.js';

defineOptions({ name: 'Error' });

/**
 * @typedef {{ statusCode?: number, message?: string }} ErrorPayload
 * @typedef {{ title: string, message: string }} StatusCodeData
 */
const props = defineProps({
  error: {
    type: Object,
    default: () => ({}),
  },
});

useHead({
  title: 'Page Not Found',
});

const mainStore = useMainStore();
const pathPrefix = computed(() => mainStore.pathPrefix);
const globalProperties = computed(() => mainStore.globals);

/** @type {Record<number, StatusCodeData>} */
const DATA_BY_STATUS_CODE = {
  404: {
    title: 'Page not found',
    message: `
    <p>We can't seem to find the page you're looking for.</p>
    <p>It might have been removed or replaced.</p>`,
  },
};

/** @type {StatusCodeData} */
const DEFAULT_ERROR_DATA = { title: 'Error', message: 'Something went wrong' };

const hasStatusCode = computed(() => Boolean(props.error.statusCode));

const dataByStatusCode = computed(() => {
  if (!hasStatusCode.value) return DEFAULT_ERROR_DATA;
  return DATA_BY_STATUS_CODE[props.error.statusCode] ?? DEFAULT_ERROR_DATA;
});

const buttonColors = computed(() => {
  const info = globalProperties.value?.info ?? {};
  const pageBuilderThemeColor = get(info, 'page_builder_theme_color.hex', '');
  const cmsHeadingColor = get(info, 'cms_heading_color.hex', '');
  return pageBuilderThemeColor || cmsHeadingColor || '#000000';
});

const homePath = computed(() => pathPrefix.value || '/');

const sanitizedErrorMessage = computed(() => {
  const raw = props.error.message || dataByStatusCode.value.message;
  return DOMPurify.sanitize(raw);
});

const statusCodeMessage = computed(() => (hasStatusCode.value ? `Error: ${props.error.statusCode}` : 'Error'));

onMounted(() => {
  if (import.meta.dev) {
    console.error(props.error.message || props.error);
  }
});
</script>

<style lang="scss" scoped>
.error-page {
  display: flex;
  justify-content: center;
  font-style: normal;
  font-size: 14px;

  .content {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    padding: 100px;
    overflow: auto;

    @media screen and (max-width: 600px) {
      padding: 50px;
    }

    #status-code {
      color: #747474;
    }

    p {
      padding: 30px 20px 50px 0px;
    }

    h1 {
      font-size: 45px;
    }

    #go-Home-wrapper {
      a {
        margin-top: 25px;
        text-decoration: none;
        color: #ffffff;
        padding: 10px 15px;
        border-radius: 3px;
        cursor: pointer;
      }
      a:focus-visible {
        outline: 2px solid -webkit-focus-ring-color;
        outline-offset: 1px;
        box-shadow: inset 0 0 0px 2px #ffffff;
      }
    }
  }
}
</style>
